import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ServiceCard from '../components/ServiceCard';
import BookingModal from '../components/BookingModal';
import { trackBookPageView } from '../utils/gtag';
import './Book.css';

// Dedicated landing page headlines per service category. Keyed by the
// `category` value in pricing_config.json (underscore form). The URL slug
// uses hyphens (jump-start) — we normalize when reading useParams.
const CATEGORY_HEADINGS = {
  standard: {
    h1: 'Mobile Oil Change at Your Driveway',
    subtitle: 'Full synthetic oil change with 5 quarts and filter — at your home, office, or anywhere in Sacramento County.'
  },
  truck: {
    h1: 'Mobile Oil Change for Trucks & SUVs',
    subtitle: 'Specialized service for pickup trucks and larger SUVs. Extra-quart oil included where needed.'
  },
  european: {
    h1: 'European Vehicle Oil Change at Your Driveway',
    subtitle: 'Specialty filters and full synthetic for BMW, Mercedes, Audi, VW and more — at your location.'
  },
  jump_start: {
    h1: 'Mobile Jump Start Service in Sacramento',
    subtitle: 'Battery dead? We come to you, same-day, anywhere in Sacramento County.'
  },
  diagnostics: {
    h1: 'Mobile Diagnostics & Check-Engine Scanning in Sacramento',
    subtitle: 'Check-engine light on? We come to you and read the codes on-site — most makes and models, including European, luxury, and many exotic vehicles.'
  },
  rv: {
    h1: 'Mobile Oil Change for RVs & Motorhomes',
    subtitle: 'Class A, B, and C motorhomes — full synthetic oil change at your storage spot, campground, or driveway. From $150.'
  }
};

const DEFAULT_HEADING = {
  h1: 'Book Your Mobile Oil Change',
  subtitle: 'Pick your service below — we come to your home, office, or anywhere in Sacramento County. Full synthetic oil, licensed & insured, 7-day guarantee.'
};

function urlSlugToCategory(slug) {
  return (slug || '').toLowerCase().replace(/-/g, '_');
}

function Book({ services }) {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const [selectedService, setSelectedService] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const isAdmin = typeof window !== 'undefined' && localStorage.getItem('adminAuth') === 'true';

  const categoryFromUrl = urlSlugToCategory(params.category);
  const categoryFromQuery = (searchParams.get('service') || '').toLowerCase();
  const activeCategory = categoryFromUrl || categoryFromQuery || null;
  const heading = (activeCategory && CATEGORY_HEADINGS[activeCategory]) || DEFAULT_HEADING;

  useEffect(() => {
    trackBookPageView();
  }, []);

  useEffect(() => {
    if (!activeCategory) return;
    const match = services.find(s => s.category === activeCategory);
    if (match) {
      setSelectedService(match);
      setShowModal(true);
    }
  }, [activeCategory, services]);

  const handleBookNow = (service) => {
    setSelectedService(service);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedService(null);
  };

  return (
    <main className="book-page">
      <section className="book-hero">
        <div className="book-hero-container">
          <h1>{heading.h1}</h1>
          <p className="book-hero-subtitle">
            {heading.subtitle}
          </p>
          <div className="book-trust-row">
            <span className="book-trust-item">✓ Licensed & Insured</span>
            <span className="book-trust-item">✓ 7-Day Guarantee</span>
            <span className="book-trust-item">✓ Full Synthetic Oil</span>
            <span className="book-trust-item">✓ Service in 1 Hour</span>
          </div>
        </div>
      </section>

      <section className="book-services">
        <div className="book-services-container">
          <h2>Select a Service</h2>
          <p className="book-services-subtitle">
            Transparent pricing. No hidden fees. Pickup truck prices may vary.
          </p>
          <div className="services-grid">
            {services.map(service => (
              <ServiceCard
                key={service.id}
                service={service}
                onBookNow={() => handleBookNow(service)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="book-info">
        <div className="book-info-container">
          <div className="book-info-card">
            <h3>How It Works</h3>
            <ol>
              <li>Pick your service and select date/time</li>
              <li>Enter your vehicle and address details</li>
              <li>Our technician arrives and completes the oil change in ~1 hour</li>
              <li>Pay securely — get back to your day</li>
            </ol>
          </div>
          <div className="book-info-card">
            <h3>Service Area</h3>
            <p>
              We cover Sacramento and surrounding areas including Elk Grove, Roseville, Folsom,
              Rancho Cordova, Citrus Heights, Carmichael, Fair Oaks, Orangevale, West Sacramento,
              Davis, and Woodland — up to 25 miles from Sacramento.
            </p>
            <p>
              Need to confirm coverage?{' '}
              <a
                href="tel:+19168188494"
                data-event="cta_call_click"
                data-cta-location="book_service_area"
              >Call</a> or{' '}
              <a
                href="sms:+19168188494"
                data-event="cta_sms_click"
                data-cta-location="book_service_area"
              >text us</a>.
            </p>
          </div>
        </div>
      </section>

      {showModal && selectedService && (
        <BookingModal
          service={selectedService}
          onClose={closeModal}
          isAdmin={isAdmin}
        />
      )}
    </main>
  );
}

export default Book;
